import { AddIcon, ListIcon } from '@treams/treams-icons';
import {
	Heading,
	Paragraph,
	GeometryObserver,
	Jumbo,
	Container,
	Callout,
	Separator,
	Button,
	QuillText,
	Skeleton,
	Subheading,
	ExpandWithPreview,
} from '@treams/treams-ui';
import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { rgba } from 'polished';
import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@treams/treams-routes';
import { useLocation } from 'react-router-dom';
import { Flex, Box } from 'reflexbox';
import styled from 'styled-components';
import _ from 'lodash';

import CompetenciesGraph from './CompetenciesGraph';
import Competency, { MiniCompetencyLevel } from './Competency';
import { ISkillsAnalysis } from './SkillsAnalysis';

function Effect<T>(props: PropsWithChildren<{ value: T; effect: (value: T) => void }>) {
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => props.effect(props.value), [props.value]);

	return props.children;
}

function GeometryObserverEffect(
	props: PropsWithChildren<{
		callback: (bounds: { top: number; height: number }) => void;
	}>,
) {
	const ref = useRef<HTMLDivElement>(null);

	return (
		<Box ref={ref}>
			<GeometryObserver innerRef={ref} scroll={false}>
				{(bounds) => (
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					<Effect value={bounds} effect={props.callback}>
						{props.children}
					</Effect>
				)}
			</GeometryObserver>
		</Box>
	);
}

const SegmentIcon = styled(Box)`
	width: 24px;
	height: 24px;
	margin-right: 2px;

	svg path {
		fill: ${(props) => props.theme.colors.slateBlue};
	}
`;

const SegmentContainer = styled(Flex)<{ selected?: boolean }>`
	line-height: 30px;
	border-radius: 15px;
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	padding: 0px 10px;
	color: ${(props) => props.theme.colors.slateBlue};
	cursor: pointer;
	align-items: center;
	flex-grow: 0;
	flex-shrink: 0;

	${(props) =>
		props.selected
			? `
        background: ${rgba(props.theme.colors.primary, 0.25)};
        border-color: transparent;
        color: ${props.theme.colors.primary};

        ${SegmentIcon} svg path {
            fill: ${props.theme.colors.primary};
        }
        `
			: `
        &:hover {
            background: ${rgba(props.theme.colors.lightBlue, 0.5)};
        }
        `}
`;

function Segment(props: {
	icon?: ReactNode;
	title: string;
	selected?: boolean;
	onClick: () => void;
}) {
	return (
		<SegmentContainer mr={2} selected={props.selected} onClick={props.onClick}>
			{props.icon && <SegmentIcon>{props.icon}</SegmentIcon>}
			{props.title}
		</SegmentContainer>
	);
}

Segment.Skeleton = styled(Skeleton.DynamicBox)`
	height: 30px;
`;

enum CompetenciesFeedbackStatus {
	EmptyCompetencies,
	EmptyAppraisals,
	Active,
}

function feedbackStatus(
	competencies: graphql.JobCompetency[],
	appraisals: graphql.CompetencyAppraisal[],
) {
	if (competencies.length === 0) {
		return CompetenciesFeedbackStatus.EmptyCompetencies;
	}

	if (appraisals.length === 0) {
		return CompetenciesFeedbackStatus.EmptyAppraisals;
	}

	return CompetenciesFeedbackStatus.Active;
}

export default function Competencies(props: { skillsAnalysis: ISkillsAnalysis; owned?: boolean }) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
	const location = useLocation();

	const [offsets, setOffsets] = useState<number[]>([]);

	const spacing = 0;

	const { appraisals: allAppraisals, jobCompetencies, competencies } = props.skillsAnalysis;
	const status = feedbackStatus(jobCompetencies, allAppraisals);

	const minLevels = new Map<string, number>();
	const appraisals = new Map(
		allAppraisals.map((appraisal) => [appraisal.competency.id, appraisal.competencyLevelIndex]),
	);

	for (const competency of jobCompetencies) {
		minLevels.set(
			competency.competency.id,
			Math.max(competency.competencyLevel, minLevels.get(competency.id) ?? 0),
		);
	}

	function levelsForCompetency(competency: graphql.Competency) {
		const expected = minLevels.get(competency.id) ?? 0;
		const actual = appraisals.get(competency.id) ?? -1;

		return competency.levels.map((level, index) => {
			if (!level.enabled || actual < 0) {
				return null;
			}

			if (index <= actual) {
				return true;
			}

			if (index > expected) {
				return null;
			}

			return false;
		});
	}

	function scrollTo(index?: number) {
		window.scrollTo({
			top: index === undefined ? 0 : offsets[index] - 48,
			behavior: 'smooth',
		});
	}

	useEffect(() => {
		function onScroll() {
			const top = 0;

			if (window.scrollY === 0) {
				setSelectedIndex(undefined);
			} else {
				const before = [top + spacing + 50]
					.concat(offsets)
					.map((y, i) => [i, y] as [number, number])
					.filter(([, y]) => y < window.scrollY + top + spacing)
					.map(([i]) => i)
					.reverse();

				setSelectedIndex(before.length >= 1 ? before[0] : undefined);
			}
		}

		window.addEventListener('scroll', onScroll);

		return () => window.removeEventListener('scroll', onScroll);
	}, [offsets]);

	const sortedCompetencies = _.sortBy(
		competencies,
		(competency) => localize(competency.title).text,
	);

	return (
		<>
			<Container pt={4} mb={-2}>
				<a href={t('skills.callout.link')} target="_blank" rel="noreferrer">
					<Callout color="blue">
						<Box>
							<strong>{t('skills.callout.new')}:</strong> {t('skills.callout.description')}
						</Box>
					</Callout>
				</a>
			</Container>
			<Jumbo
				title={
					props.skillsAnalysis.jobTitle
						? t('skills.jobTitle.pageTitle', {
								jobTitle: localize(props.skillsAnalysis.jobTitle.title).text,
						  })
						: t('competencies.gap.title')
				}
				description={!props.skillsAnalysis.jobTitle ? t('competencies.gap.description') : undefined}
				button={
					props.owned && (
						<Button
							icon={<AddIcon />}
							title={t('competencies.gap.request')}
							link={`/feedback/request/competencies?return=${location.pathname}`}
						/>
					)
				}
				footer={
					<>
						<Container mb={3} mt={-3}>
							<ExpandWithPreview preview={6}>
								{props.skillsAnalysis.jobTitle &&
									props.skillsAnalysis.jobTitle.description &&
									!QuillText.isEmpty(localize(props.skillsAnalysis.jobTitle.description).text) && (
										<Paragraph>
											<QuillText value={localize(props.skillsAnalysis.jobTitle.description).text} />
										</Paragraph>
									)}
							</ExpandWithPreview>
						</Container>
						<Separator />
					</>
				}
			/>

			<Box style={{ position: 'sticky', top: 0 }} backgroundColor="white">
				<Container py={2}>
					<Flex>
						<Segment
							title={t('competencies.gap.navigation.overview')}
							icon={<ListIcon />}
							selected={selectedIndex === undefined}
							onClick={() => scrollTo(undefined)}
						/>
						{sortedCompetencies.map((competency, index) => (
							<Segment
								key={competency.id}
								title={localize(competency.title).text}
								selected={selectedIndex === index}
								onClick={() => scrollTo(index)}
							/>
						))}
					</Flex>
				</Container>

				<Separator />
			</Box>

			<Box style={{ flexGrow: 1, flexShrink: 1 }}>
				<Box>
					<Container>
						<Flex style={{ position: 'sticky', top: spacing, height: 0 }}>
							<Box flexGrow={1} flexShrink={1} />
							<Box flexGrow={0} flexShrink={0} mt={5}>
								<CompetenciesGraph
									data={sortedCompetencies.map((competency) => ({
										title: localize(competency.title).text,
										levels: levelsForCompetency(competency),
									}))}
									selectedIndex={selectedIndex}
									onSelect={scrollTo}
								/>
							</Box>
						</Flex>

						<Flex>
							<Box flexGrow={1} flexShrink={1}>
								<Box mt={4}>
									{status === CompetenciesFeedbackStatus.EmptyCompetencies && (
										<Box mb={2}>
											<Heading>{t('skills.graph.competencies.empty.title')}</Heading>
											<Paragraph>{t('skills.graph.competencies.empty.description')}</Paragraph>
										</Box>
									)}

									{props.owned && status === CompetenciesFeedbackStatus.EmptyAppraisals && (
										<Box mb={2}>
											<Heading>{t('skills.graph.appraisals.empty.title')}</Heading>
											<Paragraph>
												<Link to={`/feedback/request/competencies?return=${location.pathname}`}>
													{t('skills.graph.appraisals.empty.link')}
												</Link>{' '}
												{t('skills.graph.appraisals.empty.description')}
											</Paragraph>
										</Box>
									)}

									{status === CompetenciesFeedbackStatus.Active && (
										<>
											<Box mb={2}>
												<Heading>{t('skills.graph.instructions.title')}</Heading>
												<Paragraph>{t('skills.graph.instructions.description')}</Paragraph>
											</Box>
											<Flex py={0.5} alignItems="center">
												<MiniCompetencyLevel active={false} color="green" />
												<Paragraph ml={2}>{t('skills.graph.instructions.reached.label')}</Paragraph>
											</Flex>
											<Flex py={0.5} alignItems="center">
												<MiniCompetencyLevel active={false} color="yellow" />
												<Paragraph ml={2}>
													{t('skills.graph.instructions.unreached.label')}
												</Paragraph>
											</Flex>
											<Flex py={0.5} alignItems="center">
												<MiniCompetencyLevel active={false} color="slateBlue" />
												<Paragraph ml={2}>
													{t('skills.graph.instructions.inactive.label')}
												</Paragraph>
											</Flex>
										</>
									)}
								</Box>

								{sortedCompetencies.map((competency, i) => (
									<Box
										key={competency.id}
										minHeight={i === competencies.length - 1 ? 'calc(100vh - 48px)' : undefined}
									>
										<GeometryObserverEffect
											callback={({ top }) =>
												setOffsets((offsets) => {
													const slice = offsets.slice();
													slice[i] = top;
													return slice;
												})
											}
										>
											<Competency
												competency={competency}
												min={minLevels.get(competency.id) ?? 0}
												levels={levelsForCompetency(competency)}
												options={competency.levels}
												owned={props.owned}
											/>
										</GeometryObserverEffect>
									</Box>
								))}
							</Box>
							<Box width={336} flexGrow={0} flexShrink={0} ml={2} />
						</Flex>
					</Container>
				</Box>
			</Box>
		</>
	);
}

Competencies.Skeleton = function CompetenciesSkeleton() {
	return (
		<>
			<Jumbo.Skeleton />
			<Separator />
			<Container py={2}>
				<Flex>
					<Segment.Skeleton mr={2} />
					<Segment.Skeleton mr={2} />
					<Segment.Skeleton />
				</Flex>
			</Container>
			<Separator />
			<Container my={4}>
				<Subheading.Skeleton mb={2} />
				<Paragraph.Skeleton />
			</Container>
		</>
	);
};
